import type { ChannelId, ChannelPolicy, OrgChannelPolicy } from '../types/channel.js';
import { getAllChannelIds } from './registry.js';

/**
 * Resolves the effective channel list for an agent by intersecting agent-level
 * channel policy with org-level channel policy.
 *
 * Rules:
 * - Agent allowlist: only listed channels allowed
 * - Agent denylist: all channels except denied ones
 * - Org allowed_channels: restricts to only those (empty = no restriction)
 * - Org denied_channels: blocks these (overrides everything)
 * - Final = (agent effective) ∩ (org effective) - (org denied)
 */
export function resolveChannels(
  agentPolicy: ChannelPolicy,
  orgPolicy: OrgChannelPolicy | undefined,
): ChannelId[] {
  // Step 1: Determine agent-effective channels
  let agentEffective: Set<ChannelId>;
  if (agentPolicy.policy === 'allowlist') {
    agentEffective = new Set(agentPolicy.allowed);
  } else {
    // denylist: all channels except denied
    const denied = new Set(agentPolicy.denied);
    agentEffective = new Set(getAllChannelIds().filter((c) => !denied.has(c)));
  }

  if (!orgPolicy) {
    return [...agentEffective];
  }

  // Step 2: Intersect with org allowlist (if non-empty)
  let result: Set<ChannelId>;
  if (orgPolicy.allowed_channels.length > 0) {
    const orgAllowed = new Set(orgPolicy.allowed_channels);
    result = new Set([...agentEffective].filter((c) => orgAllowed.has(c)));
  } else {
    result = agentEffective;
  }

  // Step 3: Remove org denied channels
  for (const denied of orgPolicy.denied_channels) {
    result.delete(denied);
  }

  return [...result];
}
